import React, { useCallback, useEffect, useMemo } from 'react';
import { Table } from 'src/ui/common/molecules/table';
import { useTranslation } from 'next-i18next';
import { INftActivitiesLocale } from 'src/features/common/collectible/types/locales';
import { useNftHistory, useFetchNftHistory } from 'src/features/common/collectible/hooks/use-nft-history';
import { useCollectible } from 'src/features/common/collectible/hooks/collectible.hooks';
import { ActivityRow } from 'src/features/common/activity/components/activity-row';
import { Typography } from 'src/ui/common/atoms/typography';
import { FlexBox } from 'src/ui/common/atoms/flex-box';
import { NftTabsEmptyState } from 'src/features/common/collectible/components/nft-tabs-empty-state';
import { StyledLoadMore } from './style';

export const NftActivitiesContainer = () => {
    const { t } = useTranslation(['actions', 'pages']);
    const nft = useCollectible();
    const history = useNftHistory();
    const fetchNftHistory = useFetchNftHistory();

    const locale = useMemo(() => ({
        ...t('nft.view.activities', { returnObjects: true, ns: 'pages' }) as INftActivitiesLocale,
        loadMore: t('misc.loadMore', { ns: 'actions' })
    }), [t]);

    const clickLoadMore = useCallback(() => {
        fetchNftHistory(nft?.id!, {
            limit: 10,
            page: history.page + 1
        });
    }, [fetchNftHistory, history.page, nft?.id]);

    useEffect(() => {
        if (!history.totalCount) {
            fetchNftHistory(nft?.id!, {
                limit: 10,
                page: 0
            });
        }
    }, [fetchNftHistory, history.totalCount, nft?.id]);

    if (!history.items?.length) {
        return (
            <NftTabsEmptyState title={t('nft.view.activities.emptyTitle', { ns: 'pages' })}>
                {t('nft.view.activities.emptyText', { ns: 'pages' })}
            </NftTabsEmptyState>
        );
    }

    return (
        <Table stretch>
            <Table.Head>
                <Table.Row>
                    <Table.HeadCell>
                        <Typography variant='title' size='sm'>{locale.transId}</Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant='title' size='sm'>{locale.transType}</Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                      <Typography variant='title' size='sm'>{locale.time}</Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant='title' size='sm'>{locale.price}</Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                       <Typography variant='title' size='sm'>{locale.from}</Typography>
                    </Table.HeadCell>
                    <Table.HeadCell>
                        <Typography variant='title' size='sm'>{locale.to}</Typography>
                    </Table.HeadCell>
                </Table.Row>
            </Table.Head>
            <Table.Body>
                {history.items?.map(item => (
                    <ActivityRow
                        key={item.id}
                        historyItem={item}
                        blockChainId={nft?.blockchainId!}
                    />
                ))}
                {history.totalCount > history.items.length && (
                    <Table.Row>
                        <Table.Col colSpan={7}>
                            <FlexBox justifyContent='center'>
                                <StyledLoadMore onClick={clickLoadMore} variant='grey'>
                                    {locale.loadMore}
                                </StyledLoadMore>
                            </FlexBox>
                        </Table.Col>
                    </Table.Row>
                )}
            </Table.Body>
        </Table>
    );
};
